import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Upload, Sparkles, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { uploadReceipt, loadDemo } from "@/lib/api";

export default function UploadReceipt() {
  const { code } = useParams();
  const navigate = useNavigate();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = reader.result;
      setPreview(b64);
      setLoading(true);
      try {
        await uploadReceipt(code, b64);
        toast.success("Bon citit cu succes");
        navigate(`/session/${code}/review`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Nu am putut citi bonul. Încearcă o poză mai clară sau folosește bonul demo.");
        setLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDemo = async () => {
    setLoading(true);
    try {
      await loadDemo(code);
      toast.success("Bon demo încărcat");
      navigate(`/session/${code}/review`);
    } catch {
      toast.error("Eroare la încărcarea demo-ului");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header subtitle="Încarcă bonul" />
      <main className="max-w-md mx-auto px-4 sm:px-6 pt-10">
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Pasul 2</span>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2">Fotografiază bonul</h1>
        <p className="text-muted-foreground mt-2">AI-ul citește produsele, prețurile, taxele și totalul automat.</p>

        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          data-testid="receipt-camera-input"
          onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="receipt-file-input"
          onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }}
        />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8"
        >
          {loading ? (
            <div className="rounded-[28px] border border-border bg-card p-10 flex flex-col items-center text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              {preview && <img src={preview} alt="bon" className="w-28 h-36 object-cover rounded-2xl mb-5 opacity-60" />}
              <Loader2 className="animate-spin text-interactive" size={32} />
              <p className="mt-4 font-display font-bold text-lg">Citesc bonul...</p>
              <p className="text-sm text-muted-foreground mt-1">Extrag produse și prețuri cu AI</p>
            </div>
          ) : (
            <div className="space-y-3">
              <button
                data-testid="open-camera-button"
                onClick={() => cameraRef.current?.click()}
                className="w-full rounded-[28px] border-2 border-dashed border-border bg-card p-10 flex flex-col items-center text-center active:scale-[0.99] transition-transform hover:border-interactive"
              >
                <span className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                  <Camera size={26} />
                </span>
                <p className="mt-4 font-display font-bold text-lg">Deschide camera</p>
                <p className="text-sm text-muted-foreground mt-1">sau alege o poză existentă</p>
              </button>

              <button
                data-testid="upload-gallery-button"
                onClick={() => galleryRef.current?.click()}
                className="w-full bg-secondary text-secondary-foreground rounded-full py-4 px-6 font-semibold active:scale-[0.98] transition-transform flex justify-center items-center gap-2"
              >
                <Upload size={18} /> Încarcă din galerie
              </button>

              <div className="flex items-center gap-3 py-2">
                <div className="h-px bg-border flex-1" />
                <span className="text-xs text-muted-foreground">sau</span>
                <div className="h-px bg-border flex-1" />
              </div>

              <button
                data-testid="use-demo-button"
                onClick={handleDemo}
                className="w-full border border-border rounded-full py-4 px-6 font-semibold active:scale-[0.98] transition-transform flex justify-center items-center gap-2"
              >
                <FileText size={18} /> Folosește un bon demo
              </button>
              <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
                <Sparkles size={12} /> Testează aplicația fără un bon real
              </p>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
